import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Separator } from './ui/separator';
import {
  MessageSquare,
  Plus,
  Trash2,
  Clock,
  User,
  FileText,
  Phone,
  Search,
  RefreshCw,
  Loader2,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const noteTypes = [
  { value: 'general', label: 'General Note', icon: MessageSquare },
  { value: 'meeting', label: 'Meeting Notes', icon: User },
  { value: 'call', label: 'Call Notes', icon: Phone },
  { value: 'due_diligence', label: 'Due Diligence', icon: Search },
  { value: 'other', label: 'Other', icon: FileText }
];

const activityIcons = {
  note_added: MessageSquare,
  status_changed: RefreshCw,
  document_uploaded: FileText,
  round_added: Plus,
  valuation_updated: Activity,
  investment_created: Plus,
  investment_updated: RefreshCw
};

export default function InvestmentNotes({ investmentId, investmentName }) {
  const { getAuthHeaders, user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const [dialogOpen, setDialogOpen] = useState(false);

  const isTeamOrAdmin = user?.role === 'admin' || user?.role === 'team_member';

  useEffect(() => {
    if (investmentId) {
      fetchData();
    }
  }, [investmentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notesRes, activitiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/investments/${investmentId}/notes`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/investments/${investmentId}/activities`, { headers: getAuthHeaders() })
      ]);
      setNotes(notesRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Failed to fetch notes/activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/investments/${investmentId}/notes`,
        {
          investment_id: investmentId,
          content: newNote,
          note_type: noteType
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Note added successfully');
      setNewNote('');
      setNoteType('general');
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/investments/${investmentId}/notes/${noteId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Note deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getNoteTypeInfo = (type) => {
    return noteTypes.find(t => t.value === type) || noteTypes[0];
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <MessageSquare className="h-4 w-4" />
          Notes & Activity
          {notes.length > 0 && (
            <Badge variant="secondary" className="ml-1 text-xs">
              {notes.length}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {investmentName} - Notes & Activity
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('notes')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'notes'
                ? 'border-[#1E3A5F] text-[#1E3A5F]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <MessageSquare className="h-4 w-4 inline mr-2" />
            Notes ({notes.length})
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'activity'
                ? 'border-[#1E3A5F] text-[#1E3A5F]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Activity className="h-4 w-4 inline mr-2" />
            Activity ({activities.length})
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : activeTab === 'notes' ? (
            <div className="space-y-4 py-4">
              {/* Add Note Form */}
              {isTeamOrAdmin && (
                <form onSubmit={handleAddNote} className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex gap-2">
                    <Select value={noteType} onValueChange={setNoteType}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {noteTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={3}
                    className="resize-none"
                  />
                  <div className="flex justify-end">
                    <Button type="submit" disabled={submitting || !newNote.trim()} size="sm">
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add Note
                    </Button>
                  </div>
                </form>
              )}

              {/* Notes List */}
              {notes.length > 0 ? (
                <div className="space-y-3">
                  {notes.map(note => {
                    const typeInfo = getNoteTypeInfo(note.note_type);
                    return (
                      <div key={note.id} className="p-4 border rounded-lg bg-white">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              <typeInfo.icon className="h-3 w-3 mr-1" />
                              {typeInfo.label}
                            </Badge>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {note.created_by_name}
                            </span>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(note.created_at)}
                            </span>
                          </div>
                          {isTeamOrAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No notes yet</p>
                  {isTeamOrAdmin && <p className="text-sm">Add a note above to get started</p>}
                </div>
              )}
            </div>
          ) : (
            /* Activity Timeline */
            <div className="py-4">
              {activities.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                  <div className="space-y-4">
                    {activities.map(activity => {
                      const Icon = activityIcons[activity.activity_type] || Activity;
                      return (
                        <div key={activity.id} className="relative pl-10">
                          <div className="absolute left-2 w-5 h-5 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center">
                            <Icon className="h-3 w-3 text-slate-500" />
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-700">{activity.description}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {activity.created_by_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(activity.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No activity recorded yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
